import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Activity,
  Globe,
  GitCompareArrows,
  Newspaper,
  Clock,
} from "lucide-react";
import { getSportBySlug, ICON_MAP } from "../data/sports";
import { SectionHeading } from "../components/SectionHeading";
import { FeatureCard } from "../components/FeatureCard";
import { EmailCapture } from "../components/EmailCapture";
import { AdSlot } from "../components/ads/AdSlot";

const FOOTBALL_MODULES = [
  {
    icon: Activity,
    title: "Free Match Analyzer",
    description: "AI-assisted breakdowns of any fixture in plain language.",
    status: "soon",
    testId: "module-analyzer",
  },
  {
    icon: Globe,
    title: "2026 World Cup Hub",
    description: "Live group standings now — plus narratives and AI storylines coming soon.",
    status: "live",
    to: "/standings",
    testId: "module-worldcup",
  },
  {
    icon: GitCompareArrows,
    title: "Team Comparison",
    description: "Head-to-head across the metrics that move matches.",
    status: "soon",
    testId: "module-comparison",
  },
  {
    icon: Newspaper,
    title: "AI Daily Digest",
    description: "A concise, AI-curated football briefing every morning.",
    status: "soon",
    testId: "module-digest",
  },
];

export default function SportPage() {
  const { slug } = useParams();
  const sport = getSportBySlug(slug);

  if (!sport) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-32 text-center">
        <h1 className="font-display text-4xl font-bold text-white">Sport not found</h1>
        <Link to="/" className="mt-6 text-sm font-medium text-brand-cyan hover:underline">
          ← Back to home
        </Link>
      </div>
    );
  }

  const Icon = ICON_MAP[sport.icon] || ICON_MAP.Trophy;
  const isActive = sport.status === "active";

  return (
    <div data-testid={`sport-page-${sport.slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-brand-navy">
        <div className="bg-grid pointer-events-none absolute inset-0 opacity-30" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Link
            to="/"
            data-testid="sport-back-link"
            className="inline-flex items-center gap-2 text-sm text-brand-silver transition-colors duration-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-brand-royal/40 bg-brand-navy/50 text-brand-cyan glow-cyan">
              <Icon className="h-8 w-8" />
            </div>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono-data text-[10px] font-semibold uppercase tracking-wider ${
                isActive ? "bg-brand-cyan/15 text-brand-cyan" : "bg-brand-navy text-brand-silver"
              }`}
            >
              {!isActive && <Clock className="h-3 w-3" />}
              {isActive ? "Live Sport" : "Coming Soon"}
            </span>
          </div>

          <h1 className="mt-6 font-display text-5xl font-black tracking-tighter text-white sm:text-6xl">
            {sport.name}
          </h1>
          <p className="mt-4 max-w-xl text-lg leading-relaxed text-brand-silver">
            {sport.tagline}
          </p>
        </div>
      </section>

      {isActive ? (
        <>
          {/* Football modules */}
          <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <SectionHeading
              overline="Football Hub"
              title="Intelligence modules"
              subtitle="The building blocks of AI First Sports football coverage. Launching in Phase 2 — join the list to get early access."
            />
            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {FOOTBALL_MODULES.map((m) => (
                <FeatureCard key={m.testId} {...m} />
              ))}
            </div>
            <div className="mt-12">
              <AdSlot variant="in-article" />
            </div>
          </section>

          {/* Early access capture */}
          <section className="mx-auto max-w-3xl px-4 pb-24 sm:px-6">
            <div className="rounded-2xl border border-brand-cyan/20 bg-brand-navy/40 p-8 text-center sm:p-10">
              <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">
                Be first when football goes live
              </h2>
              <p className="mx-auto mt-3 max-w-md text-base text-brand-silver">
                Get early access to the Match Analyzer and 2026 World Cup Hub.
              </p>
              <div className="mx-auto mt-7 max-w-md">
                <EmailCapture source={`sport-${sport.slug}`} />
              </div>
            </div>
          </section>
        </>
      ) : (
        /* Coming soon */
        <section className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:py-28">
          <div className="rounded-2xl border border-brand-navy bg-brand-navy/30 p-10 sm:p-14">
            <Clock className="mx-auto h-10 w-10 text-brand-cyan" />
            <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl">
              {sport.name} is coming soon
            </h2>
            <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-brand-silver">
              We're starting with football and the 2026 World Cup. {sport.name}{" "}
              intelligence is on the roadmap — be the first to know when it lands.
            </p>
            <div className="mx-auto mt-8 max-w-md">
              <EmailCapture source={`coming-soon-${sport.slug}`} />
            </div>
            <Link
              to="/sports/football"
              className="mt-8 inline-block text-sm font-medium text-brand-cyan transition-colors hover:text-white"
            >
              Explore live football coverage →
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
